import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import InfoOutlineIcon from '@material-ui/icons/InfoOutline';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

const styles = {
  root: {
    width: 'fit-content',
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '1.5',
    marginBottom: '8px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  fullWidth: {
    width: '100%',
  },
  icon: {
    marginRight: '12px',
    fontSize: '20px',
    flexShrink: 0,
  },
  message: {
    flex: 1,
    color: 'inherit',
  },
  // Success variant
  success: {
    backgroundColor: '#d4edda',
    color: '#155724',
    '& $icon': {
      color: '#28a745',
    },
  },
  // Info variant
  info: {
    backgroundColor: '#d1ecf1',
    color: '#0c5460',
    '& $icon': {
      color: '#17a2b8',
    },
  },
  // Warning variant
  warning: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    '& $icon': {
      color: '#ffc107',
    },
  },
  // Error variant
  error: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    '& $icon': {
      color: '#dc3545',
    },
  },
};

const icons = {
  success: CheckCircleIcon,
  info: InfoOutlineIcon,
  warning: WarningIcon,
  error: ErrorOutlineIcon,
};

/**
 * Alert component that replicates MUI v7 design
 * @param {Object} props - component props
 * @param {('success'|'info'|'warning'|'error')} props.severity - severity of the alert
 * @param {string|Element} props.children - content to display in the alert
 * @param {string} props.className - additional CSS class name
 * @param {boolean} props.fullWidth - whether the alert should be full width
 */
const Alert = ({ severity = 'info', children, className, classes, fullWidth, ...other }) => {
  const IconComponent = icons[severity];

  return (
    <div
      className={classNames(classes.root, classes[severity], className, {
        [classes.fullWidth]: fullWidth,
      })}
      role="alert"
      {...other}
    >
      <IconComponent className={classes.icon} />
      <div className={classes.message}>{children}</div>
    </div>
  );
};

Alert.propTypes = {
  severity: PropTypes.oneOf(['success', 'info', 'warning', 'error']),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  classes: PropTypes.shape({
    root: PropTypes.string,
    fullWidth: PropTypes.string,
    icon: PropTypes.string,
    message: PropTypes.string,
    success: PropTypes.string,
    info: PropTypes.string,
    warning: PropTypes.string,
    error: PropTypes.string,
  }).isRequired,
  fullWidth: PropTypes.bool,
};

export default withStyles(styles)(Alert);
